// Kanji study helpers. The kanji themselves come from the server; these are pure list operations.

export interface KanjiLike {
  id: number;
  char: string;
  level: string;
  meanings: string[];
  on: string[];
  kun: string[];
}

/** Most common first: every N5 kanji, then N4, each in the list's own order (by id). */
export function slideshowOrder<T extends KanjiLike>(list: T[]): T[] {
  const rank = (k: T) => (k.level === 'N5' ? 0 : 1);
  return list.slice().sort((a, b) => rank(a) - rank(b) || a.id - b.id);
}

/** Where a day's slideshow starts, so the first kanji changes daily instead of always being 日. */
export function slideshowStart(length: number, dateKey: string): number {
  if (length <= 0) return 0;
  const [y, m, d] = dateKey.split('-').map(Number) as [number, number, number];
  const dayNumber = Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
  return dayNumber % length;
}

/** The reading to speak or show first: on'yomi, else kun'yomi, else the character. */
export function primaryReading(k: KanjiLike): string {
  return k.on[0] || k.kun[0] || k.char;
}

/** Case-insensitive search over the character, meanings and readings. */
export function searchKanji<T extends KanjiLike>(list: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (k) =>
      k.char.indexOf(q) >= 0 ||
      k.meanings.some((m) => m.toLowerCase().indexOf(q) >= 0) ||
      k.on.some((r) => r.indexOf(q) >= 0) ||
      k.kun.some((r) => r.indexOf(q) >= 0),
  );
}

export interface QuizQuestion<T> {
  kanji: T;
  options: { text: string; correct: boolean }[];
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = a[i]!;
    a[i] = a[j]!;
    a[j] = t;
  }
  return a;
}

/**
 * "What does this kanji mean?" — up to `count` questions with four distinct choices each.
 * Needs at least four kanji with distinct first meanings; returns [] otherwise.
 */
export function quizQuestions<T extends KanjiLike>(list: T[], count: number, rng: () => number = Math.random): QuizQuestion<T>[] {
  const usable = list.filter((k) => k.meanings.length > 0);
  const distinct: Record<string, true> = {};
  for (const k of usable) distinct[k.meanings[0]!] = true;
  if (Object.keys(distinct).length < 4) return [];
  return shuffle(usable, rng)
    .slice(0, count)
    .map((k) => {
      const right = k.meanings[0]!;
      const wrong: string[] = [];
      for (const o of shuffle(usable, rng)) {
        const m = o.meanings[0]!;
        if (m !== right && wrong.indexOf(m) < 0) wrong.push(m);
        if (wrong.length === 3) break;
      }
      const options = shuffle([{ text: right, correct: true }, ...wrong.map((text) => ({ text, correct: false }))], rng);
      return { kanji: k, options };
    });
}
